import React, { useLayoutEffect, useRef, useState } from 'react'

import styles from './_spreadsheet-pane.module.scss'
import { Button, Icon, LabelAndData, Tooltip } from '../../module'
import { type LabelAndDataProps } from '../LabelAndData/LabelAndData'
import ScrollingContainer from '../ScrollingContainer/ScrollingContainer'

export type SpreadsheetPaneProps = {
  /** Text for the header */
  headerText: string
  /** Sub header label and data props */
  subHeader?: LabelAndDataProps
  /** Tooltip info for the info tooltip in the sub header */
  tooltipInfo?: string
  /** Content of the SpreadsheetPane */
  children: ({ collapsed }: { collapsed: boolean }) => React.JSX.Element
  /** Determines if the pane is expanded or collapsed */
  collapsed: boolean
  /** Toggle the collapsed state in the parent */
  setCollapsed: (collapsed: boolean) => void
  /** Optional prop to add a test id to the SpreadsheetPane for QA testing */
  qaTestId?: string
}

const SpreadsheetPane = ({
  headerText,
  subHeader,
  tooltipInfo,
  children,
  collapsed,
  setCollapsed,
  qaTestId = 'spreadsheet-pane',
}: SpreadsheetPaneProps): React.JSX.Element => {
  const spreadsheetPaneContainerRef = useRef<HTMLDivElement>(null),
    subHeaderRef = useRef<HTMLDivElement>(null),
    [contentHeight, setContentHeight] = useState(0)

  useLayoutEffect(() => {
    if (spreadsheetPaneContainerRef.current) {
      setContentHeight(
        spreadsheetPaneContainerRef.current.clientHeight -
          (subHeaderRef.current?.clientHeight ?? 0) -
          56,
      )
    }
  }, [spreadsheetPaneContainerRef.current?.clientHeight])

  return (
    <div
      className={`${styles.container} ${
        collapsed ? styles.collapsed : styles.expanded
      }`}
      ref={spreadsheetPaneContainerRef}
      qa-test-id={qaTestId}
    >
      <div className={styles.header}>
        {!collapsed ? <span>{headerText}</span> : null}
        <Button as='unstyled' onClick={() => setCollapsed(!collapsed)}>
          <Icon
            icon='popoverTriangle'
            className={`${styles.arrowIcon} ${collapsed ? styles.flipped : ''}`}
            color='dark-blue'
          />
        </Button>
      </div>
      {subHeader || tooltipInfo ? (
        <div
          className={`${
            subHeader ? styles.subHeader : styles.withoutSubHeader
          }`}
          ref={subHeaderRef}
        >
          {subHeader && !collapsed ? <LabelAndData {...subHeader} /> : null}
          {tooltipInfo ? (
            <Tooltip tooltipContent={tooltipInfo} position='left'>
              <Icon icon='info' color='dark-blue' />
            </Tooltip>
          ) : null}
        </div>
      ) : null}
      {subHeader || tooltipInfo ? (
        <div className={`${styles.divider}`} />
      ) : null}
      <ScrollingContainer height={contentHeight} hideGradients={true}>
        {children({ collapsed })}
      </ScrollingContainer>
    </div>
  )
}

const ContentBlock = ({
  children,
}: {
  children: React.ReactNode
}): React.JSX.Element => {
  return <div className={styles.contentBlock}>{children}</div>
}

const LargeAlert = ({
  children,
  color,
}: {
  children: React.ReactNode
  color: 'pink' | 'blue' | 'green' | 'red'
}) => {
  return (
    <div className={`${styles.largeAlert} ${styles[color]}`}>{children}</div>
  )
}

SpreadsheetPane.ContentBlock = ContentBlock
SpreadsheetPane.LargeAlert = LargeAlert

export { SpreadsheetPane }
